import { useState } from 'react'
import { vatSatz, vatZeichen } from '../vatMark'
import { listAuthors, deleteAuthor, type AuthorRow } from '../api'
import { countryName, moneySign } from '../lookups'
import { useTabs, useDataVersion } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import { useServerList } from '../hooks/useServerList'
import { useColumnVisibility } from '../hooks/useColumnVisibility'
import ListToolbar from '../components/ListToolbar'
import ListPanel from '../components/ListPanel'
import Pagination from '../components/Pagination'
import DataTable, { type Column } from '../components/DataTable'
import ColumnManager from '../components/ColumnManager'
import ConfirmDialog from '../components/ConfirmDialog'
import { useBulkTrash } from '../components/BulkTrash'
import { DEFAULT_TABLE_PAGE_SIZE } from '../../shared/constants'
import { fill, PAGES_AUTHORS_LIST_TEXTS as T } from '../../shared/texts'

// Authors are creditors: we settle with them monthly and transfer money. An author is created
// automatically from an article report and is incomplete at that point — the master data is
// maintained in the edit tab. The list therefore carries a "Fehlt noch" column, so a gap is
// noticed before a settlement run stumbles over it.
//
// Same table machinery as the customers list (table-list-pattern): server-side search, sort,
// paging, resizable + hideable columns. Two things it deliberately does NOT have — there is
// no "+ Neuer Autor" (an author comes from a WP article report, never from this mask) and no
// delete (authors carry settlements; there is no delete route and no trash for them).

const EditIcon = () => <img src="/icons/edit.svg" alt="" aria-hidden="true" />
const TrashIcon = () => <img src="/icons/delete.svg" alt="" aria-hidden="true" />

export default function AuthorsList() {
    const { openTab, publishChange } = useTabs()
    const { toast } = useToast()
    // Refetch when an author is saved, and when the catalogue changes — that drives
    // the "Artikel" column.
    const version = useDataVersion('authors') + useDataVersion('catalog')
    const { preferences, savePreferences, can } = useAuth()
    // Authors hang off the invoices module (see app.ts), so that is where the right lives.
    const canDelete = can('invoices', 'delete')
    const list = useServerList<AuthorRow>(listAuthors, version, {
        sort: 'displayName', dir: 'asc', pageSize: preferences.tablePageSize?.authors ?? DEFAULT_TABLE_PAGE_SIZE,
    })
    const cols = useColumnVisibility('authors')

    // Persist the chosen rows-per-page per user.
    const onPageSize = (n: number) => {
        list.setPageSize(n)
        savePreferences({ tablePageSize: { ...(preferences.tablePageSize ?? {}), authors: n } })
    }

    // Delete confirmation: the targeted author (null = dialog closed) + state.
    const [toDelete, setToDelete] = useState<AuthorRow | null>(null)
    const [delBusy, setDelBusy] = useState(false)
    const [delError, setDelError] = useState('')

    const askDelete = (a: AuthorRow) => { setDelError(''); setToDelete(a) }
    const cancelDelete = () => { if (!delBusy) { setToDelete(null); setDelError('') } }

    async function confirmDelete() {
        if (!toDelete) return
        setDelBusy(true); setDelError('')
        try {
            await deleteAuthor(toDelete._id)
            publishChange('authors')
            toast(T.movedToTrash)
            setToDelete(null)
        } catch (e: any) {
            setDelError(e?.response?.data?.error ?? T.deleteFailed)
        } finally {
            setDelBusy(false)
        }
    }

    const openEdit = (a: AuthorRow) => openTab({
        id: `author-${a._id}`,
        name: fill(T.tabName, { name: a.displayName }),
        component: 'author-form',
        config: { id: a._id },
    })

    // The default widths are measured, not guessed: each one holds its HEADING without
    // clipping (scrollWidth === clientWidth on the .th-label), and together they stay under
    // the container so the sticky action column is never pushed out of view. Shaving one
    // brings back the „LANI" that the master spotted.
    // Mehrfachauswahl → Papierkorb (components/BulkTrash.tsx): same right as the single delete.
    const bulk = useBulkTrash<AuthorRow>({
        kind: 'authors', enabled: canDelete, rows: list.rows, rowId: (r) => r._id, total: list.total,
        filter: { q: list.q },
        onDone: () => publishChange('authors'),
    })

    const columns: Column<AuthorRow>[] = [
        { key: 'displayName', header: T.colName, sortField: 'displayName', defaultWidth: 135, render: (a) => a.displayName },
        { key: 'company', header: T.colCompany, sortField: 'company', defaultWidth: 92, render: (a) => a.company || '—' },
        { key: 'externalRef', header: T.colWpId, sortField: 'externalRef', className: 'center', defaultWidth: 74, render: (a) => a.externalRef },
        { key: 'email', header: T.colEmail, sortField: 'email', defaultWidth: 118, render: (a) => a.email || '—' },
        { key: 'city', header: T.colCity, sortField: 'city', defaultWidth: 90, render: (a) => a.address?.city || '—' },
        {
            // Written out („DE" → „Deutschland"), so the column can be read without knowing the
            // codes. Sorted on the CODE, not the name — that is what the database holds, and a
            // sort that disagrees with what the server does would be worse than none.
            key: 'country', header: T.colCountry, sortField: 'country', defaultWidth: 110,
            render: (a) => countryName(a.address?.country) || '—',
        },
        {
            key: 'vatId', header: T.colVatId, sortField: 'vatId', defaultWidth: 105,
            // ⚠️ Die Farbe kommt aus `vatZeichen`, nicht aus einer eigenen `=== false`-Pruefung.
            // Die eigene faerbte die Nummer eines zypriotischen Autors rot, obwohl sie dort
            // nichts blockiert — dieselbe falsche Behauptung, die das Zeichen daneben schon
            // abgelegt hat. Eine Fassung, oder die beiden laufen wieder auseinander.
            render: (a) => a.vatId
                ? <span title={vatSatz(a)} style={{ color: vatZeichen(a).farbe }}>{a.vatId}</span>
                : <span className="muted" title={vatSatz(a)}>—</span>,
        },
        { key: 'itemCount', header: T.colItems, sortField: 'itemCount', className: 'center', defaultWidth: 82, render: (a) => a.itemCount },
        {
            // Not sortable: the effective fee is two fields, and an empty one means "the
            // brand's default applies" — a sort on either column would order by nothing.
            key: 'fee', header: T.colFee, defaultWidth: 78,
            render: (a) => a.feeType
                ? `${a.feeValue}${a.feeType === 'fixed' ? ` ${moneySign()}` : ' %'}`
                : <span className="muted" title={T.feeDefaultTitle}>{T.feeDefault}</span>,
        },
        {
            key: 'status', header: T.colStatus, sortField: 'status', defaultWidth: 80,
            render: (a) => {
                if (a.status === 'active') return T.statusActive
                // ⚠️ „geblockt" ohne Zeitpunkt kann nichts abschalten — das ist der Altbestand
                // aus der Zeit, als das Feld „pausiert" hieß und folgenlos war. Sichtbar
                // machen statt so tun, als wirke es.
                if (!a.blockedAt) {
                    return <span style={{ color: 'var(--warn)' }} title={T.blockedNoDateTitle}>{T.blockedNoDate}</span>
                }
                return <span title={fill(T.blockedSinceTitle, { date: new Date(a.blockedAt).toLocaleDateString('de-DE') })}>{T.blocked}</span>
            },
        },
        {
            // Derived on the server from five fields, so there is nothing to sort on.
            key: 'missing', header: T.colMissing, defaultWidth: 130,
            render: (a) => a.missing.length === 0
                ? <span className="muted">{T.complete}</span>
                : <span style={{ color: 'var(--danger)' }}>{a.missing.join(', ')}</span>,
        },
        {
            key: 'actions', header: T.colActions, defaultWidth: 100, sticky: true,
            render: (a) => (
                <div className="row-actions">
                    <button className="icon-action" title={T.edit} aria-label={T.edit} onClick={() => openEdit(a)}><EditIcon /></button>
                    {canDelete && <button className="icon-action" title={T.delete} aria-label={T.delete} onClick={() => askDelete(a)}><TrashIcon /></button>}
                </div>
            ),
        },
    ]
    if (bulk.column) columns.unshift(bulk.column)

    return (
        <div className="list-page">
            <ListToolbar
                title={T.title}
                infoText={T.info}
            />

            {list.error && <div className="notice err">{list.error}</div>}

            {bulk.bar}

            <ListPanel
                count={list.total}
                grandTotal={list.grandTotal}
                filtered={list.q.trim() !== ''}
                search={{ value: list.q, onChange: list.setQ, placeholder: T.searchPlaceholder }}
                columnManager={<ColumnManager columns={columns} hidden={cols.hidden} onToggle={cols.toggle} onReset={cols.reset} />}
            >
                {list.loading ? <p className="muted">{T.loading}</p> : list.rows.length === 0 ? (
                    <p className="muted">
                        {list.grandTotal === 0
                            ? T.emptyNone
                            : T.emptyFiltered}
                    </p>
                ) : (
                    <DataTable
                        key={cols.resetKey}
                        tableId="authors"
                        columns={columns}
                        hidden={cols.hidden}
                        rows={list.rows}
                        rowKey={(a) => a._id}
                        sort={list.sort}
                        dir={list.dir}
                        onSort={list.toggleSort}
                        onRowActivate={openEdit}
                    />
                )}
            </ListPanel>

            <Pagination page={list.page} pageCount={list.pageCount} onPage={list.setPage} pageSize={list.pageSize} onPageSize={onPageSize} />

            <ConfirmDialog
                open={!!toDelete}
                title={T.trashTitle}
                message={toDelete
                    ? <>
                        {fill(T.deleteQuestion, { name: toDelete.displayName })}<br />
                        {T.deleteInactiveBefore}<b>{T.deleteInactiveBold}</b>{T.deleteInactiveAfter}<br />
                        {T.deleteAdminsRestore}
                    </>
                    : null}
                confirmLabel={T.trashTitle}
                danger
                busy={delBusy}
                error={delError}
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
            />
            {bulk.dialogs}
        </div>
    )
}
